import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { EventCategory, Severity, Vendor } from '@ixam/shared';
import {
  acknowledgeEvent,
  deleteEvent,
  listEvents,
  resolveEvent,
  type EventFilters,
} from '../api/events.api';
import { listServers } from '../api/servers.api';
import { SeverityBadge } from '../components/SeverityBadge';
import { Spinner } from '../components/Spinner';
import { ConfirmDialog, useConfirm } from '../components/ConfirmDialog';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function apiErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message ?? fallback;
}

const SEVERITIES: Severity[] = ['critical', 'warning', 'info', 'ok'];
const VENDORS: Vendor[] = ['dell', 'hp', 'lenovo'];
const CATEGORIES: EventCategory[] = [
  'storage',
  'thermal',
  'power',
  'network',
  'system',
  'security',
  'other',
];

const SEVERITY_LABEL_IT: Record<Severity, string> = {
  critical: 'critico',
  warning: 'attenzione',
  info: 'info',
  ok: 'ok',
};

const CATEGORY_LABEL_IT: Record<EventCategory, string> = {
  storage: 'storage',
  thermal: 'termico',
  power: 'alimentazione',
  network: 'rete',
  system: 'sistema',
  security: 'sicurezza',
  other: 'altro',
};

export function Events() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<EventFilters>({ page: 1, limit: 50 });
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['events', filters],
    queryFn: () => listEvents(filters),
    placeholderData: (prev) => prev,
    refetchInterval: 10_000,
  });

  const { data: servers } = useQuery({
    queryKey: ['servers'],
    queryFn: listServers,
  });

  const ack = useMutation({
    mutationFn: acknowledgeEvent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      toast.success('Evento preso in carico');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Operazione fallita')),
  });
  const resolve = useMutation({
    mutationFn: resolveEvent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      toast.success('Evento risolto');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Operazione fallita')),
  });
  const del = useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      toast.success('Evento eliminato');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Eliminazione non riuscita')),
  });

  function update(patch: Partial<EventFilters>) {
    setFilters((prev) => ({ ...prev, page: 1, ...patch }));
  }

  return (
    <div className="p-5 md:p-10 max-w-7xl mx-auto space-y-6">
      <header>
        <h1 className="font-bold text-3xl text-ink">Eventi</h1>
        <p className="text-sm text-ink-muted mt-1">
          Tutti gli alert raccolti dai BMC della flotta.
        </p>
      </header>

      <div className="ixam-card p-4">
        <div className="flex flex-wrap gap-2">
          <select
            value={filters.severity ?? ''}
            onChange={(e) =>
              update({ severity: (e.target.value || undefined) as Severity | undefined })
            }
            className="ixam-input flex-1 min-w-[160px]"
          >
            <option value="">Tutte le severità</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL_IT[s]}
              </option>
            ))}
          </select>
          <select
            value={filters.vendor ?? ''}
            onChange={(e) =>
              update({ vendor: (e.target.value || undefined) as Vendor | undefined })
            }
            className="ixam-input flex-1 min-w-[160px]"
          >
            <option value="">Tutti i vendor</option>
            {VENDORS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={filters.category ?? ''}
            onChange={(e) =>
              update({ category: (e.target.value || undefined) as EventCategory | undefined })
            }
            className="ixam-input flex-1 min-w-[160px]"
          >
            <option value="">Tutte le categorie</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL_IT[c]}
              </option>
            ))}
          </select>
          <select
            value={filters.serverId ?? ''}
            onChange={(e) => update({ serverId: e.target.value || undefined })}
            className="ixam-input flex-1 min-w-[160px]"
          >
            <option value="">Tutti i server</option>
            {servers?.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Cerca nel messaggio…"
            value={filters.search ?? ''}
            onChange={(e) => update({ search: e.target.value || undefined })}
            className="ixam-input flex-1 min-w-[220px]"
          />
        </div>
      </div>

      <div className="ixam-card overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-paper-soft text-xs uppercase tracking-wide text-ink-faint">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Quando</th>
              <th className="text-left px-5 py-3 font-medium">Severità</th>
              <th className="text-left px-5 py-3 font-medium">Server</th>
              <th className="text-left px-5 py-3 font-medium">Componente</th>
              <th className="text-left px-5 py-3 font-medium">Messaggio</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {isLoading && (
              <tr>
                <td colSpan={7} className="text-center py-10 text-ink-muted">
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={16} /> Caricamento…
                  </span>
                </td>
              </tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-16 text-ink-muted">
                  Nessun evento. Sottoscrivi un server agli eventi del BMC per
                  iniziare a riceverli.
                </td>
              </tr>
            )}
            {data?.items.map((e) => {
              const server = servers?.find((s) => s._id === e.serverId);
              return (
                <tr key={e._id} className="hover:bg-paper-soft/40 align-top">
                  <td className="px-5 py-3.5 text-ink-muted whitespace-nowrap text-xs">
                    {new Date(e.occurredAt).toLocaleString('it-IT')}
                  </td>
                  <td className="px-5 py-3.5">
                    <SeverityBadge severity={e.severity} />
                  </td>
                  <td className="px-5 py-3.5 text-ink">
                    {server?.name ?? (
                      <code className="text-xs text-ink-faint">
                        {e.serverId.slice(0, 8)}…
                      </code>
                    )}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-ink-muted">
                    {e.component || '—'}
                  </td>
                  <td className="px-5 py-3.5 text-ink max-w-md">{e.message}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <StatusChips event={e} />
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      {!e.acknowledged && (
                        <button
                          title="Segna come preso in carico"
                          onClick={() => ack.mutate(e._id)}
                          className="ixam-icon-btn text-sev-info"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {!e.resolved && (
                        <button
                          title="Segna come risolto"
                          onClick={() => resolve.mutate(e._id)}
                          className="ixam-icon-btn text-sev-ok"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        title="Elimina evento"
                        onClick={() =>
                          confirm.ask({
                            title: 'Eliminare questo evento?',
                            body: `${e.severity.toUpperCase()} · ${e.message.slice(0, 120)}${
                              e.message.length > 120 ? '…' : ''
                            }`,
                            confirmLabel: 'Elimina',
                            destructive: true,
                            onConfirm: () => del.mutate(e._id),
                          })
                        }
                        className="ixam-icon-btn text-sev-critical"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {data && data.pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-line-soft flex items-center justify-between text-sm bg-paper-soft/50">
            <div className="text-ink-muted">
              Pagina {data.pagination.page} di {data.pagination.totalPages} ·{' '}
              {data.pagination.total} eventi
            </div>
            <div className="flex gap-2">
              <button
                disabled={data.pagination.page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                className="ixam-btn-ghost text-xs py-1.5 px-3"
              >
                Precedente
              </button>
              <button
                disabled={data.pagination.page >= data.pagination.totalPages}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                className="ixam-btn-ghost text-xs py-1.5 px-3"
              >
                Successiva
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog request={confirm.request} onClose={confirm.dismiss} />
    </div>
  );
}

function StatusChips({ event }: { event: { acknowledged: boolean; resolved: boolean } }) {
  if (event.resolved) {
    return (
      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-sev-ok-soft text-sev-ok border border-sev-ok/30">
        risolto
      </span>
    );
  }
  if (event.acknowledged) {
    return (
      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-sev-info-soft text-sev-info border border-sev-info/30">
        preso in carico
      </span>
    );
  }
  return (
    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-paper-soft text-ink-faint border border-line">
      aperto
    </span>
  );
}
